/**
 * Le battement d'etat du journal.
 *
 * POURQUOI CE MODULE EXISTE. Deux traces que le journal ne portait pas, et qui
 * manquaient exactement quand on en avait besoin. Elles n'ont rien a faire dans
 * la boucle de jeu: elles ne changent rien, elles observent.
 *
 * L'ENTREE ET LA SORTIE DE L'EAU. `nage` n'etait journalise nulle part. Face a
 * une session ou le proprietaire se plaignait de ne pas pouvoir entrer dans la
 * mer, le fichier montrait ses coups de joystick et les messages de deblocage,
 * mais ne disait a aucun moment s'il avait nage. Il a fallu reconstruire le
 * terrain dans un script pour trancher.
 *
 * UN BATTEMENT D'ETAT. La position n'apparaissait qu'au moment d'un tap. Entre
 * deux taps — donc pendant tout le pilotage au joystick, precisement quand les
 * ennuis arrivent — le fichier ne disait plus ou etait le bonhomme. Or la regle
 * du projet est qu'on doit pouvoir rejouer une partie de tete a partir du
 * journal: il faut un point regulier, meme quand le joueur ne touche a rien.
 */
import type { Joueur } from '../../shared/types/player.ts'
import type { Horloge } from '../../shared/sim/temps.ts'

import type { Batiments } from '../../shared/sim/monde-batiments.ts'
import { dansPiscine, dansSpa } from '../../shared/sim/batiments.ts'
import { journal } from './journal.ts'
import type { ITerrain } from '../../shared/types/world.ts'

/** Battement d'etat dans le journal, en secondes reelles. */
export const PERIODE_JOURNAL_ETAT = 5

export class BattementEtat {
  private readonly terrain: ITerrain
  private readonly batiments: Batiments
  /** Etat de nage a la frame precedente: on ne journalise que les bascules. */
  private nageAvant = false
  private minuterie = 0

  constructor(terrain: ITerrain, batiments: Batiments) {
    this.terrain = terrain
    this.batiments = batiments
  }

  battre(dt: number, j: Joueur, interieur: string | null, horloge: Horloge, indice: string | null): void {
    if (j.nage !== this.nageAvant) {
      this.nageAvant = j.nage
      journal.info('nage', j.nage ? 'entre dans l’eau' : 'sort de l’eau', {
        x: Number(j.pos.x.toFixed(1)),
        z: Number(j.pos.z.toFixed(1)),
        fond: Number(this.terrain.hauteur(j.pos.x, j.pos.z).toFixed(2)),
        piscine: dansPiscine(this.batiments.liste(), j.pos.x, j.pos.z),
      })
    }

    this.minuterie += dt
    if (this.minuterie < PERIODE_JOURNAL_ETAT) return
    this.minuterie = 0
    journal.debug('etat', `en (${j.pos.x.toFixed(1)}, ${j.pos.z.toFixed(1)}) ${j.etat}`, {
      x: Number(j.pos.x.toFixed(1)),
      z: Number(j.pos.z.toFixed(1)),
      biome: this.terrain.biome(j.pos.x, j.pos.z),
      nage: j.nage,
      vehicule: j.vehicule,
      interieur,
      // Sans lui, deux lignes du journal prises a la meme position racontent
      // deux endroits differents: le salon et la terrasse ont le meme (x, z).
      niveau: j.niveau,
      spa: dansSpa(this.batiments.liste(), j.pos.x, j.pos.z, j.niveau),
      faim: Math.round(j.besoins.faim),
      energie: Math.round(j.besoins.energie),
      heure: `${String(horloge.heure).padStart(2, '0')}:${String(horloge.minute).padStart(2, '0')}`,
      indice,
    })
  }
}
